import React, { Fragment } from "react";
import PropTypes from "prop-types";
import {
    useTable,
    useGlobalFilter,
    useAsyncDebounce,
    useSortBy,
    useFilters,
    useExpanded,
    usePagination,
    useRowSelect
} from "react-table";
import { Table, Row, Col, Button, Input, CardBody } from "reactstrap";
import { DefaultColumnFilter } from "./filters";

// Define a default UI for filtering
function GlobalFilter({
    globalFilter,
    setGlobalFilter,
    SearchPlaceholder,
}) {
    const [value, setValue] = React.useState(globalFilter);
    const onChange = useAsyncDebounce((value) => {
        setGlobalFilter(value || undefined);
    }, 200);

    return (
        <React.Fragment>
            <CardBody>
                <form>
                    <Row className="g-3">
                        <Col>
                            <div className="search-box me-2 mb-2 d-inline-block col-12">
                                <input
                                    onChange={(e) => {
                                        setValue(e.target.value);
                                        onChange(e.target.value);
                                    }}
                                    id="search-bar-0"
                                    type="text"
                                    className="form-control search /"
                                    placeholder={SearchPlaceholder}
                                    value={value || ""}
                                />
                                <i className="bx bx-search-alt search-icon"></i>
                            </div>
                        </Col>
                    </Row>
                </form>
            </CardBody>

        </React.Fragment>
    );
}


const TableContainerUser = ({
    columns,
    data,
    isPagination,
    isGlobalSearch,
    isGlobalFilter,
    onRowDoubleClick,
    customPageSize,
    tableClass,
    theadClass,
    trClass,
    thClass,
    divClass,
    SearchPlaceholder,
    // Hotfix 11 — optional custom global-filter function. Use this when
    // columns are marked `disableFilters: true` (which would otherwise
    // strip them from react-table's default global-filter scan, making
    // the search box appear "weak" / unable to find anything).
    globalFilterFn,
}) => {
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page,
        prepareRow,
        canPreviousPage,
        canNextPage,
        pageOptions,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state,
        preGlobalFilteredRows,
        setGlobalFilter,
        state: { pageIndex, pageSize },
    } = useTable(
        {
            columns,
            data,
            // Round 35 — compact EPO-style column filter (replaces the stock
            // "search (N) ..." input so all tables look consistent).
            defaultColumn: {
                Filter: ({ column: { filterValue, setFilter } }) => (
                    <input
                        className="form-control form-control-sm"
                        style={{ fontSize: 12, minWidth: 70 }}
                        value={filterValue || ""}
                        onChange={(e) => setFilter(e.target.value || undefined)}
                        placeholder="Filter…"
                        aria-label="Filter column"
                    />
                ),
            },
            // Hotfix 11 — when a custom globalFilterFn is passed, route
            // it into react-table's useTable config so it overrides the
            // default behavior (which respects column.canFilter and thus
            // skips disableFilters=true columns).
            globalFilter: globalFilterFn,
            // HR-UAT-3 root cause (2026-08-24, UAT pass 5): the previous
            // initialState carried sortBy: [{ desc: false }] — a sort entry
            // with NO column id. In react-table 7.8.0 that malformed entry
            // REVERSES every dataset rendered through this container:
            //   • useSortBy's passthrough guard `!sortBy.length` fails
            //     (length is 1), so sorting runs;
            //   • the entry is then dropped by the "non existing columns"
            //     filter (no column has id === undefined), leaving ZERO
            //     comparators;
            //   • defaultOrderByFn's final fallback
            //       `dirs[0] ? rowA.index - rowB.index : rowB.index - rowA.index`
            //     sees dirs[0] === undefined and returns
            //     rowB.index - rowA.index for every pair — a full reversal.
            // Net effect: the HR roster arrived from the API newest-hire-
            // FIRST (ORDER BY HireDate DESC) and rendered newest-hire-LAST.
            // sortBy starts EMPTY now, so rows render in exactly the order
            // the API returns; header-click sorting (where a table enables
            // it) still works because clicks create well-formed {id, desc}
            // entries.
            initialState: {
                pageIndex: 0, pageSize: 10, selectedRowIds: 0, sortBy: [],
            },
        },
        useGlobalFilter,
        useFilters,
        useSortBy,
        useExpanded,
        usePagination,
        useRowSelect
    );

    const generateSortingIndicator = (column) => {
        return column.isSorted ? (column.isSortedDesc ? <span style={{ fontSize: "24px", fontWeight: "900" }}>&#8593;</span> : <span style={{ fontSize: "24px", fontWeight: "900" }}>&#8595;</span>) : "";
    };

    const onChangeInSelect = (event) => {
        setPageSize(Number(event.target.value));
    };
    const onChangeInInput = (event) => {
        const page = event.target.value ? Number(event.target.value) - 1 : 0;
        gotoPage(page);
    };

    return (
        <Fragment>
            {(isGlobalSearch || isGlobalFilter) && (
                <Row className="mb-3">
                    {isGlobalSearch && (
                        <Col md={1}>
                            <select
                                className="form-select"
                                value={pageSize}
                                onChange={onChangeInSelect}
                            >
                                {[10, 20, 30, 40, 50].map((pageSize) => (
                                    <option key={pageSize} value={pageSize}>
                                        Show {pageSize}
                                    </option>
                                ))}
                            </select>

                        </Col>
                    )}
                    {isGlobalFilter && (
                        <GlobalFilter
                            preGlobalFilteredRows={preGlobalFilteredRows}
                            globalFilter={state.globalFilter}
                            setGlobalFilter={setGlobalFilter}
                            SearchPlaceholder={SearchPlaceholder}
                        />
                    )}

                </Row>


            )}


            <div className={divClass}>
                <Table hover {...getTableProps()} className={tableClass}>
                    <thead className={theadClass}>
                        {headerGroups.map((headerGroup) => (
                            <tr className={trClass} key={headerGroup.id}  {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map((column) => (
                                    <th key={column.id} className={thClass} {...column.getSortByToggleProps()}>
                                        {column.render("Header")}
                                        {generateSortingIndicator(column)}
                                        {/* <Filter column={column} /> */}
                                    </th>
                                ))}
                            </tr>

                        ))}
                        {/* Round 32 — per-column filter row (the EPO-style strip).
                            Renders only when at least one column is filterable, so
                            other tables sharing this container are unaffected. */}
                        {headerGroups.map((headerGroup) =>
                            headerGroup.headers.some((c) => c.canFilter) ? (
                                <tr key={`${headerGroup.id}-filters`} className="dk-filter-row" style={{ background: "#fafbfc" }}>
                                    {headerGroup.headers.map((column) => (
                                        <th key={`${column.id}-f`} style={{ fontWeight: 400, padding: "4px 6px" }}>
                                            {column.canFilter ? column.render("Filter") : null}
                                        </th>
                                    ))}
                                </tr>
                            ) : null
                        )}
                    </thead>

                    <tbody {...getTableBodyProps()}>
                        {page.map((row) => {
                            prepareRow(row);

                            return (
                                <tr
                                    key={row.id}
                                    {...row.getRowProps()}
                                    onDoubleClick={() => onRowDoubleClick?.(row)}
                                    style={{ cursor: "pointer" }}
                                >
                                    {row.cells.map((cell) => (
                                        <td key={cell.id} {...cell.getCellProps()}>
                                            {cell.render("Cell")}
                                        </td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            </div>

            {isPagination &&
                <Row className="justify-content-md-end justify-content-center align-items-center p-2">
                    <Col className="col-md-auto">
                        <div className="d-flex gap-1">
                            <Button
                                color="primary"
                                onClick={previousPage}
                                disabled={!canPreviousPage}
                            >
                                {"<"}
                            </Button>
                        </div>
                    </Col>
                    <Col className="col-md-auto d-none d-md-block">
                        Page{" "}
                        <strong>
                            {pageIndex + 1} of {pageOptions.length}
                        </strong>
                    </Col>
                    <Col className="col-md-auto">
                        <Input
                            type="number"
                            min={1}
                            style={{ width: 70 }}
                            max={pageOptions.length}
                            defaultValue={pageIndex + 1}
                            onChange={onChangeInInput}
                        />
                    </Col>

                    <Col className="col-md-auto">
                        <div className="d-flex gap-1">
                            <Button color="primary" onClick={nextPage} disabled={!canNextPage}>
                                {">"}
                            </Button>
                        </div>
                    </Col>
                </Row>
            }
        </Fragment>
    );
};



TableContainerUser.propTypes = {
    preGlobalFilteredRows: PropTypes.any,
};

export default TableContainerUser;